import React, { useState, useRef, useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type GridApi, type RowClassParams, type IRowNode, type RowSelectionOptions } from "ag-grid-community";
import type { StateUpdatedEvent, GridReadyEvent, CellValueChangedEvent, SelectionChangedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from "@mui/material";
import Barcode from "react-barcode";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import gridInputCellEditor from "@/components/aggrid/aggrid_input_cell_editor";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_stock_info.scoped.scss";

interface State {
  gridApi: GridApi | null,
  currIndex: number,
  inoutType: string,
  skuStockInfos: any[],
  remainQty: string,
  jobQty: string
}

const defState: State = {
  gridApi: null,
  currIndex: 0,
  inoutType: "",
  skuStockInfos: [],
  remainQty: "0",
  jobQty: "0"
};

const validationMsg = [
  {id: "workReqErpNo", type: "InvalidStatus", msg: "작업대기 상태만 작업지시할 수 있습니다."},
  {id: "workInfos", type: "NotFound", msg: "선택SKU가 있는 작업만 작업지시할 수 있습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: (_p: any, e: any) => `Tracking ID(${e.value}) 현재 작업대에서 호출할 수 없는 SKU입니다.`},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

const defColDef = { ...kutil.defaultColDef, sortable: false, cellDataType: false };

function PopupStockInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_stock_info";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const isAutoSelect = useRef(false);
  const remainQtyRef = useRef("0");
  const inoutTypeRef = useRef("");

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "창고", field: "wh", width: 100, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", field: "pn", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 110, hide: state.inoutType !== "O", cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number,
      cellClass: (p: any) => (inoutTypeRef.current !== "I" && p.data.possYn == "Y") ? "input-cell ta-right" : "",
      editable: (p: any) => inoutTypeRef.current !== "I" && p.data.possYn == "Y",
      singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 }
    },
    { headerName: "바코드", field: "barcode", width: 200, hide: state.inoutType !== "I", cellStyle: {textAlign: "center"},
      cellRenderer: (p: any) => {
        if (!p.data.skuNo) return null;
        return (
          <Barcode value={p.data.skuNoEx} format="CODE128" width={1} height={28} displayValue={false} background="#f7f7f7" margin={0} />
        );
      }
    },
    { headerName: "작업중", field: "workStationId", width: 100, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        if (p.data.possYn == "N") {
          return (p.data.workStationId ? commonCode.getCodeValue("workStationId", p.data.workStationId) : "금지CELL");
        }
      }
    }
  ], [state.inoutType]);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowStyle: (p: RowClassParams) => {
      if (p.data.possYn == "N") {
        return {"backgroundColor": "#FFCBCB"};
      }
    },
    selectionColumnDef: {
      resizable: true
    },
    stopEditingWhenCellsLoseFocus: true
  }), []);
  const rowSelection = useMemo((): RowSelectionOptions => ({
    mode: state.inoutType === "I" ? "singleRow" : "multiRow",
    isRowSelectable: (n: IRowNode) => n.data.possYn == "Y",
    hideDisabledCheckboxes: true
  }), [state.inoutType]);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowDataUpdated: (e: any) => {
      autoSelectRows(e.api);
    },
    onCellValueChanged: (e: CellValueChangedEvent) => {
      if (e.colDef.field === "jobQty") {
        e.node.setSelected(!!e.newValue && Number(e.newValue) > 0);
        updateJobQty(e.api);
      }
    },
    onSelectionChanged: (e: SelectionChangedEvent) => {
      if (isAutoSelect.current) return;

      const isIn = inoutTypeRef.current === "I";
      e.api.forEachNode((node) => {
        if (!node.isSelected()) {
          if (node.data.jobQty) node.setData({ ...node.data, jobQty: "" });
        } else if (isIn && !Number(node.data.jobQty)) {
          node.setData({ ...node.data, jobQty: remainQtyRef.current });
        }
      });
      updateJobQty(e.api);
    },
  });

  useEffect(() => {
    init();
  }, []);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!props.isShow) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [props.isShow]);
  // 끝: 바코드 스캔 기능 적용

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function searchInfos(index = 0) {
    const currInfo = props.data.infos[index];
    remainQtyRef.current = currInfo.remainQty;
    inoutTypeRef.current = currInfo.inoutType;
    setState(prev => ({ ...prev, currIndex: index, inoutType: currInfo.inoutType, skuStockInfos: currInfo.skuStockInfos, remainQty: currInfo.remainQty, jobQty: "0" }));
  }

  async function closePopup() {
    const nextIndex = state.currIndex + 1;
    if (nextIndex < props.data.infos.length) {
      await searchInfos(nextIndex);
    } else {
      props.onClose({ isSuccess: true });
    }
  }

  function updateJobQty(api: GridApi) {
    const total = api.getSelectedNodes().reduce((sum, node) => sum + (Number(node.data.jobQty) || 0), 0);
    setState(prev => ({ ...prev, jobQty: String(total) }));
  }
  function autoSelectRows(api: GridApi) {
    const isIn = inoutTypeRef.current === "I";
    let remaining = Number(remainQtyRef.current) || 0;
    const targets: IRowNode[] = [];

    isAutoSelect.current = true;
    api.deselectAll();
    api.forEachNode((node) => {
      if (node.data.possYn !== "Y" || remaining <= 0) {
        node.setData({ ...node.data, jobQty: "" });
        return;
      }
      const qty = isIn ? remaining : Math.min(Number(node.data.stockQty) || 0, remaining);
      if (qty <= 0) {
        node.setData({ ...node.data, jobQty: "" });
        return;
      }
      remaining -= qty;
      node.setData({ ...node.data, jobQty: qty });
      targets.push(node);
    });
    api.setNodesSelected({ nodes: targets, newValue: true });
    isAutoSelect.current = false;

    updateJobQty(api);
  }

  async function save() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("SKU를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      if (!info.jobQty || Number(info.jobQty) < 1) {
        kutil.alert("작업수량을 입력해 주세요.");
        return;
      } else {
        if (state.inoutType == "O" && Number(info.jobQty) > Number(info.stockQty)) {
          kutil.alert("재고수량을 초과하였습니다.");
          return;
        }
      }
    }

    if (Number(state.jobQty) > Number(state.remainQty)) {
      kutil.alert("지시수량을 초과하였습니다.");
      return;
    }

    const currInfo = props.data.infos[state.currIndex];
    try {
      currInfo.choiceSkuType = "M";
      currInfo.choiceSkuCallQty = selectedInfos.length;
      currInfo.choiceSkuStockQty = currInfo.skuStockInfos.length;
      const data = await kutil.sendPost("/api/inoutbound/inout_work.proc?mode=orderWork", { ...currInfo, workInfos: selectedInfos }, {
        validationMsg: validationMsg
      });
    } catch(e) {
      console.log(e);
      return;
    }

    const nextIndex = state.currIndex + 1;
    if (nextIndex < props.data.infos.length) {
      await searchInfos(nextIndex);
    } else {
      props.onClose({ isSuccess: true });
    }
  }

  function selectSkuNoEx(skuNoEx: string) {
    const api = state.gridApi;
    if (!api) return;

    const isIn = inoutTypeRef.current === "I";
    let found = false;
    let selected = false;
    api.forEachNode((node) => {
      if (node.data?.skuNoEx !== skuNoEx) return;
      found = true;
      if (!node.selectable || (isIn && selected)) return;

      node.setSelected(true, isIn);
      api.ensureNodeVisible(node, "middle");
      selected = true;
    });

    if (!found) {
      kutil.alert("일치하는 SKU No가 없습니다.\r\n" + skuNoEx);
    } else if (!selected) {
      kutil.alert("현재 작업대에서 호출할 수 없는 SKU입니다.\r\n" + skuNoEx);
    }
  }

  async function onBarcodeScan(code: string) {
    if (code == "SKU_SELECT") {
      save();
    } else {
      selectSkuNoEx(code);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog 
        sx={{"& .MuiDialog-container": {"& .MuiPaper-root": {width: "100%", maxWidth: "1400px",},},}}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          재고정보
        </DialogTitle>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 상단 정보 */}
            <div>
              <div>
                <p>지시수량</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={valueFormatter.number(state.remainQty)}
                />
              </div>
              <div>
                <p>작업수량</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={valueFormatter.number(state.jobQty)}
                />
              </div>
            </div>
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={state.skuStockInfos}
                  defaultColDef={defColDef}
                  initialState={gridState}
                  getRowStyle={gridOptions.getRowStyle}
                  rowSelection={rowSelection}
                  selectionColumnDef={gridOptions.selectionColumnDef}
                  stopEditingWhenCellsLoseFocus={gridOptions.stopEditingWhenCellsLoseFocus}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                  onRowDataUpdated={gridCallbacks.onRowDataUpdated}
                  onCellValueChanged={gridCallbacks.onCellValueChanged}
                  onSelectionChanged={gridCallbacks.onSelectionChanged}
                />
              </div>
            </AgGridProvider>
            {/* 하단 바코드 */}
            { auth.write &&
            <div className="popup_barcode">
              <Barcode value="SKU_SELECT" format="CODE128" width={1} height={28} displayValue={false} background="#f7f7f7" margin={0} />
              <div>
                <i></i>
                확인
              </div>
            </div>
            }
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            확인
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupStockInfo);
